
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  address: string;
  status: string;
  assigned_agent: string;
  configuration: string;
  comments: string;
  created_at: string;
  updated_at: string;
  m2_habitable: string;
  systeme_chauffage: string;
  installateur: string;
  nombre_personnes: string;
  categorie_revenu: string;
  rdv_date?: string;
  rdv_time?: string;
  rdv_notes?: string;
  custom_fields: Record<string, string>;
  assigned_user?: {
    id: string;
    name: string;
    email: string;
  };
}

interface Status {
  id: string;
  label: string;
  color: string;
  order_index: number;
}

interface Configuration {
  id: string;
  name: string;
}

interface Installateur {
  id: string;
  name: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
}

export default function Clients({ currentUser }: { currentUser: any }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [installateurs, setInstallateurs] = useState<Installateur[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [installateurFilter, setInstallateurFilter] = useState('');
  const [showClientForm, setShowClientForm] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'agent' as 'admin' | 'agent',
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    codePostal: '',
    ville: '',
    zoneClimatique: '',
    status: 'prospect',
    assignedAgent: currentUser.role === 'agent' ? currentUser.id : '',
    configuration: '',
    comments: '',
    m2Habitable: '',
    systemeChauffage: '',
    installateur: '',
    nombrePersonnes: '',
    categorieRevenu: '',
    rdvDate: '',
    rdvTime: '',
    rdvNotes: '',
    customFields: {} as Record<string, string>,
  });

  // Fonction pour déterminer la zone climatique basée sur le code postal
  const getZoneClimatique = (codePostal: string): string => {
    if (!codePostal || codePostal.length < 2) return '';
    
    const departement = codePostal.substring(0, 2);
    
    // Zone H1
    const zoneH1 = [
      '01', '02', '03', '05', '08', '10', '14', '15', '19', '21', '23', '25', '27', '28',
      '38', '39', '42', '43', '45', '51', '52', '54', '55', '57', '58', '59', '60', '61',
      '62', '63', '67', '68', '69', '70', '71', '73', '74', '75', '76', '77', '78', '80',
      '87', '88', '89', '90', '91', '92', '93', '94', '95'
    ];
    
    // Zone H2
    const zoneH2 = [
      '04', '07', '09', '12', '16', '17', '18', '22', '24', '26', '29', '31', '32', '33',
      '35', '36', '37', '40', '41', '44', '46', '47', '48', '49', '50', '53', '56', '64',
      '65', '72', '79', '81', '82', '84', '85', '86'
    ];
    
    // Zone H3
    const zoneH3 = [
      '06', '11', '13', '20', '30', '34', '66', '83'
    ];
    
    // Codes spéciaux
    if (codePostal.startsWith('975')) return 'H1'; // St Pierre et Miquelon
    if (codePostal.startsWith('971')) return 'H3'; // Guadeloupe
    if (codePostal.startsWith('972')) return 'H3'; // Martinique
    if (codePostal.startsWith('973')) return 'H3'; // Guyane
    if (codePostal.startsWith('974')) return 'H3'; // La Réunion
    if (codePostal.startsWith('976')) return 'H3'; // Mayotte
    
    if (zoneH1.includes(departement)) return 'H1';
    if (zoneH2.includes(departement)) return 'H2';
    if (zoneH3.includes(departement)) return 'H3';
    
    return '';
  };

  // Gérer le changement de code postal et mise à jour automatique de la zone
  const handleCodePostalChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      codePostal: value,
      zoneClimatique: getZoneClimatique(value)
    }));
  };

  // Charger toutes les données depuis Supabase avec filtrage par société
  const loadData = async () => {
    try {
      setLoading(true);

      const storedUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (!storedUser.company_id) {
        setError('Société non trouvée');
        return;
      }

      // Charger les clients avec company_id
      const clientsResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=list&company_id=${storedUser.company_id}`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const clientsResult = await clientsResponse.json();

      // Charger les statuts avec company_id
      const statusResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=statuses&company_id=${storedUser.company_id}`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const statusResult = await statusResponse.json();

      // Charger les configurations avec company_id
      const configResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=configurations&company_id=${storedUser.company_id}`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const configResult = await configResponse.json();

      // Charger les installateurs avec company_id
      const installateurResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=installateurs&company_id=${storedUser.company_id}`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const installateurResult = await installateurResponse.json();

      // Charger les utilisateurs (pas de filtre de company_id côté API)
      const usersResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management?action=list`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const usersData = await usersResponse.json();
      if (usersData.success) {
        // Filtrer les utilisateurs par société
        const companyUsers = usersData.users.filter(
          (user: any) => user.company_id === storedUser.company_id
        );
        setUsers(companyUsers);
      }

      if (clientsResult.success) setClients(clientsResult.clients || []);
      if (statusResult.success) setStatuses(statusResult.statuses || []);
      if (configResult.success) setConfigurations(configResult.configurations || []);
      if (installateurResult.success) setInstallateurs(installateurResult.installateurs || []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonctions auxiliaires pour création / mise à jour / suppression avec company_id
  const createClient = async (clientData: any) => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${storedUser.company_id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(clientData),
        }
      );
      return response;
    } catch (err) {
      console.error('Erreur lors de la création du client:', err);
      throw err;
    }
  };

  const updateClient = async (clientData: any) => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('currentUser') || '');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${storedUser.company_id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(clientData),
        }
      );
      return response;
    } catch (err) {
      console.error('Erreur lors de la mise à jour du client:', err);
      throw err;
    }
  };

  const deleteClient = async (clientId: string) => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${storedUser.company_id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: clientId }),
        }
      );
      return response;
    } catch (err) {
      console.error('Erreur lors de la suppression du client:', err);
      throw err;
    }
  };

  // Charger les données au démarrage et configurer la synchronisation en temps réel
  useEffect(() => {
    loadData();

    // Actualiser les données toutes les 90 secondes pour la synchronisation optimisée
    const interval = setInterval(loadData, 90000); // 90 secondes au lieu de 2 secondes

    return () => clearInterval(interval);
  }, []);

  // Filter clients based on role and filters
  let filteredClients = clients;

  if (currentUser.role === 'agent') {
    // Les agents ne voient que leurs propres clients
    filteredClients = clients.filter((client) => client.assigned_agent === currentUser.id);
  } else if (currentUser.role === 'call_center') {
    // Les call centers voient leurs propres clients + ceux des agents assignés
    const assignedAgents = currentUser.assigned_agents || [];
    filteredClients = clients.filter(
      (client) => client.assigned_agent === currentUser.id || assignedAgents.includes(client.assigned_agent)
    );
  }
  // Les admins voient tous les clients (pas de filtre)

  if (searchTerm) {
    filteredClients = filteredClients.filter(
      (client) =>
        client.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.phone.includes(searchTerm)
    );
  }

  if (statusFilter) {
    filteredClients = filteredClients.filter((client) => client.status === statusFilter);
  }

  if (agentFilter) {
    filteredClients = filteredClients.filter((client) => client.assigned_agent === agentFilter);
  }

  if (installateurFilter) {
    filteredClients = filteredClients.filter((client) => client.installateur === installateurFilter);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation spéciale pour RDV
    if (formData.status === 'rdv-fixe') {
      if (!formData.rdvDate) {
        alert('La date du rendez-vous est obligatoire pour un RDV fixé');
        return;
      }
      if (!formData.rdvTime) {
        alert('L\'heure du rendez-vous est obligatoire pour un RDV fixé');
        return;
      }
    }
    
    setLoading(true);

    try {
      // Construire l'adresse complète
      const adresseComplete = `${formData.address}${formData.codePostal ? ', ' + formData.codePostal : ''}${formData.ville ? ' ' + formData.ville : ''}`;
      
      const requestBody = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        address: adresseComplete,
        codePostal: formData.codePostal,
        ville: formData.ville,
        zoneClimatique: formData.zoneClimatique,
        status: formData.status,
        assignedAgent: formData.assignedAgent,
        configuration: formData.configuration,
        comments: formData.comments,
        m2Habitable: formData.m2Habitable,
        systemeChauffage: formData.systemeChauffage,
        installateur: formData.installateur,
        nombrePersonnes: formData.nombrePersonnes,
        categorieRevenu: formData.categorieRevenu,
        rdvDate: formData.rdvDate,
        rdvTime: formData.rdvTime,
        rdvNotes: formData.rdvNotes,
        customFields: formData.customFields,
      };

      let response;
      if (editingClient) {
        // Update existing client
        response = await updateClient({ id: editingClient.id, ...requestBody });
      } else {
        // Create new client
        response = await createClient(requestBody);
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erreur HTTP:', response.status, errorText);
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      if (!result.success) {
        console.error('Erreur dans la réponse:', result);
        alert('Erreur: ' + (result.error || 'Erreur inconnue'));
        return;
      }

      // Recharger les données après modification
      await loadData();
      setShowClientForm(false);
      setEditingClient(null);
      resetForm();
    } catch (error) {
      console.error('Erreur détaillée:', error);

      // Gestion d'erreur améliorée et sécurisée
      let errorMessage =
        'Erreur de connexion au serveur. Veuillez vérifier votre connexion et réessayer.';

      if (error instanceof Error) {
        errorMessage = 'Erreur de connexion: ' + error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        const errObj = error as any;
        if (errObj.message) {
          errorMessage = 'Erreur: ' + errObj.message;
        } else if (errObj.error) {
          errorMessage = 'Erreur: ' + errObj.error;
        }
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      address: '',
      codePostal: '',
      ville: '',
      zoneClimatique: '',
      status: 'prospect',
      assignedAgent: currentUser.role === 'agent' ? currentUser.id : '',
      configuration: '',
      comments: '',
      m2Habitable: '',
      systemeChauffage: '',
      installateur: '',
      nombrePersonnes: '',
      categorieRevenu: '',
      rdvDate: '',
      rdvTime: '',
      rdvNotes: '',
      customFields: {},
    });
  };

  // Gestion de l'ajout du premier utilisateur
  const handleAddFirstUser = () => {
    setShowUserForm(true);
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(userFormData),
      });

      const result = await response.json();
      if (result.success) {
        alert('Utilisateur créé avec succès !');
        await loadData(); // Recharger les données
        setShowUserForm(false);
        resetUserForm();
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const resetUserForm = () => {
    setUserFormData({ name: '', email: '', password: '', role: 'agent' });
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    
    // Extraire code postal et ville de l'adresse si possible
    const addressParts = client.address.split(', ');
    let codePostal = '';
    let ville = '';
    let adresseRue = client.address;
    
    if (addressParts.length > 1) {
      const lastPart = addressParts[addressParts.length - 1];
      const codePostalMatch = lastPart.match(/^(\d{5})\s*(.*)$/);
      if (codePostalMatch) {
        codePostal = codePostalMatch[1];
        ville = codePostalMatch[2];
        adresseRue = addressParts.slice(0, -1).join(', ');
      }
    }
    
    setFormData({
      firstName: client.first_name,
      lastName: client.last_name,
      phone: client.phone,
      email: client.email,
      address: adresseRue,
      codePostal: codePostal,
      ville: ville,
      zoneClimatique: getZoneClimatique(codePostal),
      status: client.status,
      assignedAgent: client.assigned_agent,
      configuration: client.configuration,
      comments: client.comments,
      m2Habitable: client.m2_habitable || '',
      systemeChauffage: client.systeme_chauffage || '',
      installateur: client.installateur || '',
      nombrePersonnes: client.nombre_personnes || '',
      categorieRevenu: client.categorie_revenu || '',
      rdvDate: client.rdv_date || '',
      rdvTime: client.rdv_time || '',
      rdvNotes: client.rdv_notes || '',
      customFields: client.custom_fields || {},
    });
    setShowClientForm(true);
  };

  const deleteClientHandler = async (clientId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      setLoading(true);
      try {
        const response = await deleteClient(clientId);
        const result = await response.json();
        if (result.success) {
          await loadData();
        } else {
          alert('Erreur: ' + result.error);
        }
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    }
  };

  const exportToCSV = () => {
    const headers = ['Prénom', 'Nom', 'Téléphone', 'Email', 'Adresse', 'Statut', 'Configuration', 'Agent'];
    const csvContent = [
      headers.join(','),
      ...filteredClients.map((client) => {
        const status = statuses.find((s) => s.id === client.status);
        const agent = users.find((u) => u.id === client.assigned_agent);
        return [
          client.first_name,
          client.last_name,
          client.phone,
          client.email,
          `"${client.address}"`,
          status?.label || '',
          client.configuration,
          agent?.name || '',
        ].join(',');
      }),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'clients.csv';
    link.click();
  };

  const getCategorieRevenuLabel = (categorie: string) => {
    const categories = {
      bleu: 'Bleu',
      jaune: 'Jaune',
      viollet: 'Viollet',
      rose: 'Rose',
    };
    return categories[categorie as keyof typeof categories] || categorie;
  };

  const getCategorieRevenuColor = (categorie: string) => {
    const colors = {
      bleu: '#3B82F6',
      jaune: '#F59E0B',
      viollet: '#8B5CF6',
      rose: '#EC4899',
    };
    return colors[categorie as keyof typeof colors] || '#6B7280';
  };

  if (loading && clients.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Chargement des clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header and filters */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des clients</h1>
          <div className="flex items-center space-x-2">
            <p className="text-gray-600">{filteredClients.length} clients</p>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-1"></div>
              <span className="text-xs text-blue-600">Sync 90s</span>
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button onClick={exportToCSV} variant="secondary">
            <i className="ri-download-line mr-2"></i>
            Exporter CSV
          </Button>
          <Button onClick={() => setShowClientForm(true)}>
            <i className="ri-add-line mr-2"></i>
            Ajouter
          </Button>
        </div>
      </div>

      {/* Message d'information amélioré */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <i className="ri-database-2-line text-green-600 mr-2"></i>
            <div>
              <p className="text-green-800 text-sm font-semibold">
                ✅ Synchronisation Supabase optimisée !
              </p>
              <p className="text-green-700 text-xs">
                Clients, statuts, configurations et installateurs synchronisés - Mise à jour toutes les 90 secondes
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-xs">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-1"></div>
              <span className="text-blue-600">Sync 90s</span>
            </div>
            <div className="text-green-600 font-mono">
              {statuses.length} statuts | {configurations.length} configs | {installateurs.length} installateurs
            </div>
          </div>
        </div>
      </div>

      {/* Section spéciale pour les sociétés sans utilisateurs */}
      {users.filter((u) => u.active).length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-add-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Aucun utilisateur dans votre société</h3>
            <p className="text-blue-700 mb-4">
              Commencez par ajouter un premier utilisateur pour gérer vos clients
            </p>
            <Button onClick={handleAddFirstUser}>
              <i className="ri-user-add-line mr-2"></i>
              Ajouter le premier utilisateur
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Input
            placeholder="Rechercher par nom ou téléphone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select
            options={[
              { value: '', label: 'Tous les statuts' },
              ...statuses.map((status) => ({
                value: status.id,
                label: status.label,
              })),
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filtrer par statut"
          />
          <Select
            options={[
              { value: '', label: 'Tous les installateurs' },
              ...installateurs.map((installateur) => ({
                value: installateur.name,
                label: installateur.name,
              })),
            ]}
            value={installateurFilter}
            onChange={setInstallateurFilter}
            placeholder="Filtrer par installateur"
          />
          {currentUser.role === 'admin' && (
            <Select
              options={[
                { value: '', label: 'Tous les agents' },
                ...users
                  .filter((user) => (user.role === 'agent' || user.role === 'call_center') && user.active)
                  .map((user) => ({
                    value: user.id,
                    label: `${user.name} (${user.role === 'call_center' ? 'Call Center' : 'Agent'})`,
                  })),
              ]}
              value={agentFilter}
              onChange={setAgentFilter}
              placeholder="Filtrer par agent"
            />
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setAgentFilter('');
              setInstallateurFilter('');
            }}
          >
            <i className="ri-refresh-line mr-2"></i>
            Réinitialiser
          </Button>
        </div>
      </div>

      {/* Client list */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Configuration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Installateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Catégorie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  RDV
                </th>
                {currentUser.role === 'admin' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernière mise à jour
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClients.map((client) => {
                const status = statuses.find((s) => s.id === client.status);
                const agent = users.find((u) => u.id === client.assigned_agent) || client.assigned_user;
                return (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {client.first_name} {client.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{client.address}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm text-gray-900">{client.phone}</p>
                        <p className="text-sm text-gray-500">{client.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                        style={{ backgroundColor: status?.color }}
                      >
                        {status?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {client.configuration}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm text-gray-900">{client.installateur || '-'}</p>
                        {client.m2_habitable && (
                          <p className="text-sm text-gray-500">
                            {client.m2_habitable}m² • {client.nombre_personnes} pers.
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {client.categorie_revenu && (
                        <span
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                          style={{
                            backgroundColor: getCategorieRevenuColor(client.categorie_revenu),
                          }}
                        >
                          {getCategorieRevenuLabel(client.categorie_revenu)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {client.rdv_date && client.status === 'rdv-fixe' ? (
                        <div className="text-sm">
                          <div className="flex items-center text-blue-600">
                            <i className="ri-calendar-check-line mr-1"></i>
                            <span>{new Date(client.rdv_date).toLocaleDateString('fr-FR')}</span>
                          </div>
                          {client.rdv_time && (
                            <div className="flex items-center text-gray-600 mt-1">
                              <i className="ri-time-line mr-1"></i>
                              <span>{client.rdv_time}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    {currentUser.role === 'admin' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <p>{agent?.name}</p>
                          {agent?.role === 'call_center' && (
                            <span className="text-xs text-purple-600">Call Center</span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(client.updated_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedClient(client)}
                        className="text-blue-600 hover:text-blue-900 cursor-pointer mr-3"
                        title="Voir les détails"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                      <button
                        onClick={() => handleEdit(client)}
                        className="text-green-600 hover:text-green-900 cursor-pointer mr-3"
                        title="Modifier"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={() => deleteClientHandler(client.id)}
                        className="text-red-600 hover:text-red-900 cursor-pointer"
                        title="Supprimer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Client form modal */}
      {showClientForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-5xl mx-4 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingClient ? 'Modifier le client' : 'Nouveau client'}
              </h2>
              <button
                onClick={() => {
                  setShowClientForm(false);
                  setEditingClient(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Informations personnelles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom *
                    </label>
                    <Input
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Entrez le prénom"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Entrez le nom"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone *
                    </label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Entrez le numéro de téléphone"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Entrez l'adresse email"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse (rue et numéro) *
                    </label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Numéro et nom de rue"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal *
                    </label>
                    <Input
                      value={formData.codePostal}
                      onChange={(e) => handleCodePostalChange(e.target.value)}
                      placeholder="Code postal (5 chiffres)"
                      pattern="[0-9]{5}"
                      maxLength={5}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville *
                    </label>
                    <Input
                      value={formData.ville}
                      onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                      placeholder="Nom de la ville"
                      required
                    />
                  </div>

                  {formData.zoneClimatique && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Zone climatique (automatique)
                      </label>
                      <div className="flex items-center bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <i className="ri-temp-cold-line text-blue-600 mr-2"></i>
                        <span className="text-blue-800 font-semibold">Zone {formData.zoneClimatique}</span>
                        <span className="text-blue-600 text-sm ml-2">
                          (Détectée automatiquement)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Housing info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Informations logement</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">M² habitable</label>
                    <Input
                      type="number"
                      value={formData.m2Habitable}
                      onChange={(e) => setFormData({ ...formData, m2Habitable: e.target.value })}
                      placeholder="Surface habitable en m²"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Système de chauffage</label>
                    <Input
                      value={formData.systemeChauffage}
                      onChange={(e) =>
                        setFormData({ ...formData, systemeChauffage: e.target.value })
                      }
                      placeholder="Type de chauffage actuel"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre de personnes dans le foyer
                    </label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.nombrePersonnes}
                      onChange={(e) =>
                        setFormData({ ...formData, nombrePersonnes: e.target.value })
                      }
                      placeholder="Nombre de personnes"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Catégorie de revenu
                    </label>
                    <Select
                      options={[
                        { value: '', label: 'Sélectionner une catégorie' },
                        { value: 'bleu', label: 'Bleu' },
                        { value: 'jaune', label: 'Jaune' },
                        { value: 'viollet', label: 'Viollet' },
                        { value: 'rose', label: 'Rose' },
                      ]}
                      value={formData.categorieRevenu}
                      onChange={(value) => setFormData({ ...formData, categorieRevenu: value })}
                    />
                  </div>
                </div>
              </div>

              {/* Commercial info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Informations commerciales</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <Select
                      options={statuses.map((status) => ({
                        value: status.id,
                        label: status.label,
                      }))}
                      value={formData.status}
                      onChange={(value) => setFormData({ ...formData, status: value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Configuration</label>
                    <Select
                      options={configurations.map((config) => ({
                        value: config.name,
                        label: config.name,
                      }))}
                      value={formData.configuration}
                      onChange={(value) => setFormData({ ...formData, configuration: value })}
                      placeholder="Sélectionnez une configuration"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Installateur</label>
                    <Select
                      options={installateurs.map((installateur) => ({
                        value: installateur.name,
                        label: installateur.name,
                      }))}
                      value={formData.installateur}
                      onChange={(value) => setFormData({ ...formData, installateur: value })}
                      placeholder="Sélectionnez un installateur"
                    />
                  </div>

                  {currentUser.role === 'admin' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Agent assigné
                      </label>
                      <Select
                        options={[
                          { value: '', label: 'Aucun agent assigné' },
                          ...users
                            .filter((user) => (user.role === 'agent' || user.role === 'call_center') && user.active)
                            .map((user) => ({
                              value: user.id,
                              label: `${user.name} (${user.email}) - ${user.role === 'call_center' ? 'Call Center' : 'Agent'}`,
                            })),
                        ]}
                        value={formData.assignedAgent}
                        onChange={(value) => setFormData({ ...formData, assignedAgent: value })}
                      />
                    </div>
                  )}

                  {currentUser.role === 'call_center' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Agent assigné
                      </label>
                      <Select
                        options={[
                          { value: currentUser.id, label: `Moi-même (${currentUser.name})` },
                          ...(currentUser.assigned_agents || [])
                            .map((agentId: string) => users.find((u) => u.id === agentId))
                            .filter(Boolean)
                            .map((user) => ({
                              value: user!.id,
                              label: `${user!.name} (${user!.email})`,
                            })),
                        ]}
                        value={formData.assignedAgent}
                        onChange={(value) => setFormData({ ...formData, assignedAgent: value })}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* RDV info - Affiché seulement si statut RDV fixé */}
              {formData.status === 'rdv-fixe' && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Informations rendez-vous
                    <span className="text-red-500 ml-1">*</span>
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                      <i className="ri-alarm-warning-line text-red-600 mr-2"></i>
                      <p className="text-red-800 text-sm font-medium">
                        La date et l'heure sont obligatoires pour un RDV fixé
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date du RDV *
                      </label>
                      <Input
                        type="date"
                        value={formData.rdvDate}
                        onChange={(e) => setFormData({ ...formData, rdvDate: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        required={formData.status === 'rdv-fixe'}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Heure du RDV *
                      </label>
                      <Select
                        options={[
                          { value: '', label: 'Sélectionner une heure' },
                          ...Array.from({ length: 22 }, (_, i) => {
                            const hour = Math.floor(i / 2) + 8;
                            const minute = i % 2 === 0 ? '00' : '30';
                            const timeString = `${hour.toString().padStart(2, '0')}:${minute}`;
                            return {
                              value: timeString,
                              label: timeString,
                            };
                          }),
                        ]}
                        value={formData.rdvTime}
                        onChange={(value) => setFormData({ ...formData, rdvTime: value })}
                      />
                      {formData.status === 'rdv-fixe' && !formData.rdvTime && (
                        <p className="text-red-500 text-xs mt-1">Heure obligatoire</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes du RDV</label>
                      <textarea
                        value={formData.rdvNotes}
                        onChange={(e) => setFormData({ ...formData, rdvNotes: e.target.value })}
                        placeholder="Notes, adresse précise, instructions spéciales..."
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commentaires</label>
                <textarea
                  value={formData.comments}
                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  placeholder="Entrez vos commentaires..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowClientForm(false);
                    setEditingClient(null);
                    resetForm();
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit">{editingClient ? 'Modifier' : 'Créer'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client details modal */}
      {selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Fiche client - {selectedClient.first_name} {selectedClient.last_name}
              </h2>
              <button
                onClick={() => setSelectedClient(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Personal info */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Informations personnelles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedClient.first_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedClient.last_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedClient.phone}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedClient.email}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedClient.address}</p>
                </div>
              </div>
            </div>

            {/* Housing info */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Informations logement</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">M² habitable</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {selectedClient.m2_habitable || 'Non renseigné'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Système de chauffage</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {selectedClient.systeme_chauffage || 'Non renseigné'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de personnes</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {selectedClient.nombre_personnes || 'Non renseigné'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie de revenu</label>
                  {selectedClient.categorie_revenu ? (
                    <span
                      className="inline-flex px-3 py-1 text-sm font-semibold rounded-full text-white"
                      style={{ backgroundColor: getCategorieRevenuColor(selectedClient.categorie_revenu) }}
                    >
                      {getCategorieRevenuLabel(selectedClient.categorie_revenu)}
                    </span>
                  ) : (
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">Non renseigné</p>
                  )}
                </div>
              </div>
            </div>

            {/* Commercial info */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Informations commerciales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Configuration</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedClient.configuration}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Installateur</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedClient.installateur || 'Non assigné'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <span
                    className="inline-flex px-3 py-1 text-sm font-semibold rounded-full text-white"
                    style={{
                      backgroundColor: statuses.find((s) => s.id === selectedClient.status)?.color,
                    }}
                  >
                    {statuses.find((s) => s.id === selectedClient.status)?.label}
                  </span>
                </div>
                {currentUser.role === 'admin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent assigné</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {users.find((u) => u.id === selectedClient.assigned_agent)?.name}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* RDV info */}
            {selectedClient.status === 'rdv-fixe' && (selectedClient.rdv_date || selectedClient.rdv_time) && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Informations rendez-vous</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date du RDV</label>
                    <div className="flex items-center bg-blue-50 p-2 rounded">
                      <i className="ri-calendar-check-line text-blue-600 mr-2"></i>
                      <p className="text-sm text-blue-800 font-medium">
                        {selectedClient.rdv_date
                          ? new Date(selectedClient.rdv_date).toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })
                          : 'Non définie'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Heure du RDV</label>
                    <div className="flex items-center bg-blue-50 p-2 rounded">
                      <i className="ri-time-line text-blue-600 mr-2"></i>
                      <p className="text-sm text-blue-800 font-medium">{selectedClient.rdv_time || 'Non définie'}</p>
                    </div>
                  </div>
                  {selectedClient.rdv_notes && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes du RDV</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedClient.rdv_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Commentaires</label>
              <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg min-h-[80]">
                {selectedClient.comments || 'Aucun commentaire'}
              </p>
            </div>

            {Object.keys(selectedClient.custom_fields || {}).length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Champs personnalisés</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(selectedClient.custom_fields).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 p-2 rounded">
                      <p className="text-xs font-medium text-gray-600">{key}</p>
                      <p className="text-sm text-gray-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t pt-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <p>
                    <strong>Créé le:</strong>{' '}
                    {new Date(selectedClient.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <p>
                    <strong>Modifié le:</strong>{' '}
                    {new Date(selectedClient.updated_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="secondary" onClick={() => setSelectedClient(null)}>
                Fermer
              </Button>
              <Button
                onClick={() => {
                  setSelectedClient(null);
                  handleEdit(selectedClient);
                }}
              >
                <i className="ri-edit-line mr-2"></i>
                Modifier
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de formulaire utilisateur */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Ajouter un utilisateur</h2>
              <button
                onClick={() => {
                  setShowUserForm(false);
                  resetUserForm();
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet *</label>
                <Input
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                  placeholder="Entrez le nom complet"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <Input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  placeholder="Entrez l'adresse email"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                <Input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  placeholder="Entrez le mot de passe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle *</label>
                <Select
                  options={[
                    { value: 'agent', label: 'Agent de téléprospection' },
                    { value: 'admin', label: 'Administrateur' },
                  ]}
                  value={userFormData.role}
                  onChange={(value) =>
                    setUserFormData({ ...userFormData, role: value as 'admin' | 'agent' })
                  }
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowUserForm(false);
                    resetUserForm();
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Création...
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-line mr-2"></i>
                      Créer l'utilisateur
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
